// Credencial de licencia firmada (etapa 8B): un JWT ES256 que el servidor entrega a cada
// dispositivo. Trae las fechas del plan (prueba, pago, gracia, solo lectura, interruptor manual),
// así el dispositivo cambia de estado en la fecha exacta aunque no tenga conexión, y no se puede
// falsificar: la firma se verifica con la clave pública incluida en la app.
//
// Sin dependencias: WebCrypto (navegador, Node y Deno). ES256 = ECDSA P-256 + SHA-256, firma en
// formato r||s (el que devuelve WebCrypto).
import { base64UrlToBytes, bytesToBase64Url } from './encoding.ts';
import type { LicenseStatus, LicenseTerms } from './license.ts';

/** Validez de cada credencial: sin conectarse en ese tiempo, el dispositivo pide conexión. */
export const LICENSE_TOKEN_DAYS = 7;
/** Tolerancia de reloj entre el dispositivo y el servidor. */
export const CLOCK_TOLERANCE_MS = 5 * 60_000;

export interface LicenseTokenPayload {
  iss: 'pos-saas';
  /** Negocio. */
  sub: string;
  /** Dispositivo. */
  aud: string;
  /** Usuario que la pidió. */
  uid: string;
  jti: string;
  /** Segundos (como todo JWT). */
  iat: number;
  exp: number;
  /** Hora del servidor al emitirla (ms). */
  srv: number;
  lic: LicenseTerms;
  plan: { code: string; name: string; limits: Record<string, number | null>; features: Record<string, unknown> };
}

export interface LicensePublicKey {
  kid: string;
  x: string;
  y: string;
}

export interface LicensePrivateKey extends LicensePublicKey {
  d: string;
}

const ALG = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const SIGN = { name: 'ECDSA', hash: 'SHA-256' } as const;
const text = new TextEncoder();

const encodeJson = (value: unknown) => bytesToBase64Url(text.encode(JSON.stringify(value)));
const decodeJson = (value: string): unknown => JSON.parse(new TextDecoder().decode(base64UrlToBytes(value)));

/** Par de claves nuevo (para el script de alta de claves y los tests). */
export async function generateLicenseKeys(kid: string): Promise<{ privateKey: LicensePrivateKey; publicKey: LicensePublicKey }> {
  const pair = (await crypto.subtle.generateKey(ALG, true, ['sign', 'verify'])) as CryptoKeyPair;
  const jwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
  if (!jwk.x || !jwk.y || !jwk.d) throw new Error('No se pudo exportar la clave.');
  return { privateKey: { kid, x: jwk.x, y: jwk.y, d: jwk.d }, publicKey: { kid, x: jwk.x, y: jwk.y } };
}

export async function signLicenseToken(payload: LicenseTokenPayload, key: LicensePrivateKey): Promise<string> {
  const cryptoKey = await crypto.subtle.importKey('jwk', { kty: 'EC', crv: 'P-256', x: key.x, y: key.y, d: key.d }, ALG, false, ['sign']);
  const signingInput = `${encodeJson({ alg: 'ES256', typ: 'JWT', kid: key.kid })}.${encodeJson(payload)}`;
  const signature = new Uint8Array(await crypto.subtle.sign(SIGN, cryptoKey, text.encode(signingInput)));
  return `${signingInput}.${bytesToBase64Url(signature)}`;
}

const STATUSES: ReadonlyArray<LicenseStatus> = ['activa', 'en_gracia', 'solo_lectura', 'suspendida'];

function isPayload(value: unknown): value is LicenseTokenPayload {
  const p = value as Partial<LicenseTokenPayload> | null;
  return (
    !!p &&
    p.iss === 'pos-saas' &&
    typeof p.sub === 'string' &&
    typeof p.aud === 'string' &&
    typeof p.jti === 'string' &&
    typeof p.iat === 'number' &&
    typeof p.exp === 'number' &&
    typeof p.srv === 'number' &&
    !!p.lic &&
    typeof p.lic.grace_days === 'number' &&
    typeof p.lic.readonly_days === 'number' &&
    (p.lic.manual_override === null || STATUSES.includes(p.lic.manual_override)) &&
    !!p.plan
  );
}

/**
 * Verifica la firma y la forma de una credencial. Devuelve el contenido, o null si es inválida
 * (firma que no coincide, clave desconocida, datos alterados). Las fechas las controla quien llama.
 */
export async function verifyLicenseToken(token: string, keys: readonly LicensePublicKey[]): Promise<LicenseTokenPayload | null> {
  try {
    const [header, body, signature] = token.split('.');
    if (!header || !body || !signature) return null;
    const h = decodeJson(header) as { alg?: string; kid?: string };
    if (h.alg !== 'ES256') return null;
    const key = keys.find((k) => k.kid === h.kid);
    if (!key) return null;
    const cryptoKey = await crypto.subtle.importKey('jwk', { kty: 'EC', crv: 'P-256', x: key.x, y: key.y }, ALG, false, ['verify']);
    const ok = await crypto.subtle.verify(SIGN, cryptoKey, base64UrlToBytes(signature) as BufferSource, text.encode(`${header}.${body}`));
    if (!ok) return null;
    const payload = decodeJson(body);
    return isPayload(payload) ? payload : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Reloj del dispositivo
// ---------------------------------------------------------------------------

/**
 * Lo que el dispositivo recuerda de su reloj para que atrasar la hora no "estire" la licencia:
 *  - la hora más alta que vio (si la hora actual es bastante menor, la atrasaron),
 *  - la hora del servidor en el último contacto y el tiempo de uso acumulado desde entonces
 *    (la app sumó minutos abierta: aunque congelen el reloj, el tiempo avanza).
 */
export interface DeviceClock {
  /** Hora más alta vista en el dispositivo (ms). */
  max_seen: number;
  /** Hora del servidor y del dispositivo en el último contacto (ms). */
  server_at_contact: number;
  device_at_contact: number;
  /** Tiempo con la app abierta desde el último contacto (ms, reloj monotónico). */
  usage_ms: number;
}

/**
 * Al hablar con el servidor el reloj vuelve a ser confiable: se reinicia todo (también la hora
 * más alta vista, por si el dispositivo estaba adelantado y después se corrigió solo).
 */
export function clockAtContact(serverMs: number, deviceMs: number): DeviceClock {
  return {
    max_seen: deviceMs,
    server_at_contact: serverMs,
    device_at_contact: deviceMs,
    usage_ms: 0,
  };
}

/** Suma tiempo de uso (medido con performance.now) y la hora vista. */
export function tickClock(clock: DeviceClock, deviceMs: number, usageDeltaMs: number): DeviceClock {
  return { ...clock, max_seen: Math.max(clock.max_seen, deviceMs), usage_ms: clock.usage_ms + Math.max(0, usageDeltaMs) };
}

/**
 * Hora "confiable" estimada: la del dispositivo corregida con la diferencia que tenía con el
 * servidor, y nunca menos que la del último contacto más el tiempo de uso. rolledBack = alguien
 * atrasó el reloj más que la tolerancia.
 */
export function trustedNow(clock: DeviceClock, deviceMs: number): { now: number; rolledBack: boolean } {
  const rolledBack = deviceMs < clock.max_seen - CLOCK_TOLERANCE_MS;
  const byClock = deviceMs + (clock.server_at_contact - clock.device_at_contact);
  const byUsage = clock.server_at_contact + clock.usage_ms;
  return { now: Math.max(byClock, byUsage), rolledBack };
}
